/**
 * API client — semua request ke oktaafd-be.
 * Auth menggunakan httpOnly cookie yang di-set saat login,
 * sehingga tidak ada token yang dipegang oleh JavaScript.
 */

const BASE = import.meta.env.VITE_API_URL

// Flag untuk mencegah multiple refresh request berjalan bersamaan
let isRefreshing = false

/**
 * Wrapper fetch yang otomatis:
 * 1. Menyertakan cookie di setiap request (credentials: include)
 * 2. Jika respons 401, coba refresh token sekali lalu retry
 * 3. Jika refresh gagal, redirect ke /login
 */
async function fetchWithAuth(path: string, options: RequestInit = {}, retry = true): Promise<Response> {
  const isFormData = options.body instanceof FormData
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: isFormData
      ? (options.headers as Record<string, string> | undefined) ?? {}
      : {
          'Content-Type': 'application/json',
          ...(options.headers as Record<string, string>),
        },
  })

  if (res.status === 401 && retry && !isRefreshing) {
    isRefreshing = true
    try {
      const refreshRes = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      if (refreshRes.ok) {
        isRefreshing = false
        return fetchWithAuth(path, options, false)
      }
    } catch {
      // Abaikan error refresh
    }
    isRefreshing = false
    if (window.location.pathname !== '/login') {
      window.location.href = '/login'
    }
    throw new Error('Session expired')
  }

  return res
}

async function get<T>(path: string): Promise<T> {
  const res = await fetchWithAuth(path)
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error ?? 'Request failed')
  }
  return res.json() as Promise<T>
}

async function patch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetchWithAuth(path, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error ?? 'Request failed')
  }
  return res.json() as Promise<T>
}

async function postForm<T>(path: string, formData: FormData): Promise<T> {
  const res = await fetchWithAuth(path, { method: 'POST', body: formData })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error ?? 'Upload gagal')
  }
  return res.json() as Promise<T>
}

// ── Auth API ───────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string
  email: string
  display_name?: string | null
  avatar_url?: string | null
  [key: string]: unknown
}

export interface UserProfileResponse {
  id: string
  email: string | null
  display_name: string | null
  avatar_url: string | null
}

export interface UpdateProfileRequest {
  display_name?: string | null
  avatar_url?: string | null
}

export const authApi = {
  login: async (email: string, password: string): Promise<void> => {
    const res = await fetch(`${BASE}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error((err as { error?: string }).error ?? 'Login gagal')
    }
  },

  logout: async (): Promise<void> => {
    await fetch(`${BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    })
  },

  me: async (): Promise<AuthUser> => {
    const res = await fetch(`${BASE}/auth/me`, { credentials: 'include' })
    if (!res.ok) throw new Error('Not authenticated')
    return res.json() as Promise<AuthUser>
  },
}

// ── Profile API ────────────────────────────────────────────────────────────────

export const profileApi = {
  getProfile: (): Promise<UserProfileResponse> =>
    get<UserProfileResponse>('/users/profile'),

  updateProfile: (data: UpdateProfileRequest): Promise<UserProfileResponse> =>
    patch<UserProfileResponse>('/users/profile', data),

  uploadAvatar: (webpBlob: Blob): Promise<{ url: string }> => {
    const fd = new FormData()
    fd.append('file', webpBlob, `avatar-${Date.now()}.webp`)
    return postForm<{ url: string }>('/users/avatar', fd)
  },
}

// ── Image Upload API ───────────────────────────────────────────────────────────

export const imageApi = {
  uploadImage: (webpBlob: Blob): Promise<{ url: string }> => {
    const fd = new FormData()
    fd.append('file', webpBlob, `img-${Date.now()}.webp`)
    return postForm<{ url: string }>('/uploads/image', fd)
  },
}

// ── Site Settings API ──────────────────────────────────────────────────────────

export interface NavLink {
  label: string
  href: string
}

export interface SiteSettings {
  bg_image_url:      string | null
  about_name:        string | null
  about_position:    string | null
  about_website_url: string | null
  about_email:       string | null
  about_location:    string | null
  about_instagram:   string | null
  about_twitter:     string | null
  about_tiktok:      string | null
  about_facebook:    string | null
  about_spotify:     string | null
  about_wa_channel:  string | null
  about_photo_url:   string | null
  about_banner_url:  string | null
  nav_links:         NavLink[] | null
}

export interface AboutUpdateRequest {
  about_name?:        string
  about_position?:    string
  about_website_url?: string
  about_email?:       string
  about_location?:    string
  about_instagram?:   string
  about_twitter?:     string
  about_tiktok?:      string
  about_facebook?:    string
  about_spotify?:     string
  about_wa_channel?:  string
  about_photo_url?:   string
  about_banner_url?:  string
}

const EMPTY_SETTINGS: SiteSettings = {
  bg_image_url: null, about_name: null, about_position: null,
  about_website_url: null, about_email: null, about_location: null,
  about_instagram: null, about_twitter: null, about_tiktok: null,
  about_facebook: null, about_spotify: null, about_wa_channel: null,
  about_photo_url: null, about_banner_url: null,
  nav_links: null,
}

export const siteApi = {
  // Plain fetch (no auth) — dipanggil dari homepage publik
  getSettings: async (): Promise<SiteSettings> => {
    const res = await fetch(`${BASE}/settings`)
    if (!res.ok) return EMPTY_SETTINGS
    return res.json() as Promise<SiteSettings>
  },

  // Requires auth — dipanggil dari admin
  updateBgImage: (url: string): Promise<SiteSettings> =>
    patch<SiteSettings>('/settings/bg-image', { url }),

  updateAbout: (data: AboutUpdateRequest): Promise<AboutUpdateRequest> =>
    patch<AboutUpdateRequest>('/settings/about', data),

  updateNavLinks: (links: NavLink[]): Promise<{ nav_links: NavLink[] }> =>
    patch<{ nav_links: NavLink[] }>('/settings/nav', { nav_links: links }),
}
